/**
 * Le module **Tarification** — plans versionnés, simulateur, rejeu, mode
 * fantôme et rentabilité (`EX-TAR-024`, `EX-TAR-026`, `EX-TAR-027`).
 *
 * Maquette : artboard `b_tarification`. `test/app/pricing-maquette.test.tsx`
 * relit cette maquette et la confronte au livré (`D-34`).
 *
 * ## Ce que ce chantier referme — le plus gros du dépôt
 *
 * `PricingGovernanceApi` servait **sept routes, éprouvées, appelées par aucun
 * écran**. C'est le module qui en portait le plus de toute la famille `D-25` /
 * `D-30` / `D-35` / `D-45` / `D-57`.
 *
 * ## Le motif est « catalogue + résultat », et la version est le PIVOT
 *
 * On choisit un plan, puis une version. Tout le reste — l'historique
 * d'activation, la comparaison, le rejeu, le fantôme, les gestes — porte sur
 * **cette version-là**. Un écran qui aurait mis la simulation au même niveau
 * aurait confondu deux gestes : instruire une grille, et interroger le tarif
 * d'une opération. Le simulateur est donc une section à part.
 *
 * ## RIEN NE PART TOUT SEUL
 *
 * Le rejeu parcourt le trafic réel : c'est la requête la plus lourde du
 * back-office. Le lancer au choix d'une version le déclencherait à chaque clic
 * dans le rail. Il attend un geste, comme la production d'un état du module
 * Reporting attend le sien — et pour la même raison, écrite là-bas.
 *
 * ## `unpriced` NE SE TAIT JAMAIS
 *
 * C'est le point de cet écran. Une opération que la version candidate ne sait
 * pas tarifer n'est **pas** une opération à zéro franc : c'est un trou dans la
 * grille. Un delta de revenu lu sans ce compte serait un chiffre rassurant tiré
 * d'une grille incomplète — et c'est sur ce chiffre qu'on décide d'activer.
 *
 * ## DEUX GESTES SUR CINQ SONT ABSENTS, et c'est déclaré
 *
 * Le socle exécute cinq actions. Les promotions — créer, arrêter — portent sur
 * un objet qu'**aucune route ne liste** : les offrir exigerait de connaître une
 * référence par avance, soit la moitié d'une interface. C'est le défaut que le
 * module Clients a payé, et que la liquidité a payé une seconde fois dans ce
 * même chantier. Voir `deferred-work.md`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  activationModes,
  comparePlanVersions,
  fetchPlanActivations,
  fetchPricingPlans,
  fetchProfitability,
  fetchShadowObservation,
  performPricingAction,
  pricingActions,
  replayPricing,
  simulatePricing,
} from '../http/pricing.js';
import type {
  PlanActivation,
  PricingPlan,
  PricingReplay,
  PricingSimulation,
  PlanVersion,
  Profitability,
  RuleComparison,
  ShadowObservation,
} from '../http/pricing.js';

/** Le module Tarification. */
export function PricingScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [plans, setPlans] = useState<readonly PricingPlan[] | null>(null);
  const [erreurPlans, setErreurPlans] = useState<string | null>(null);
  const [planChoisi, setPlanChoisi] = useState<string | null>(null);
  const [versionChoisie, setVersionChoisie] = useState<string | null>(null);

  const [activations, setActivations] = useState<readonly PlanActivation[] | null>(null);
  const [rejeu, setRejeu] = useState<PricingReplay | null>(null);
  const [fantome, setFantome] = useState<ShadowObservation | null>(null);
  const [comparaison, setComparaison] = useState<readonly RuleComparison[] | null>(null);
  const [simulation, setSimulation] = useState<PricingSimulation | null>(null);
  const [rentabilite, setRentabilite] = useState<Profitability | null>(null);

  const [erreurImpact, setErreurImpact] = useState<string | null>(null);
  const [erreurSimulation, setErreurSimulation] = useState<string | null>(null);
  const [erreurRentabilite, setErreurRentabilite] = useState<string | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const relireLesPlans = useCallback(async (): Promise<void> => {
    try {
      const catalogue = await fetchPricingPlans(client);
      setPlans(catalogue);
      setErreurPlans(null);
    } catch (echec) {
      setPlans(null);
      setErreurPlans(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  useEffect(() => {
    void relireLesPlans();
  }, [relireLesPlans]);

  // La rentabilité se lit au montage : c'est une LECTURE, elle ne pèse pas sur
  // le trafic réel comme le rejeu. Un refus n'efface que sa propre section.
  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const vue = await fetchProfitability(client);
        if (vivant) {
          setRentabilite(vue);
          setErreurRentabilite(null);
        }
      } catch (echec) {
        if (!vivant) return;
        setRentabilite(null);
        setErreurRentabilite(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  const plan = useMemo(
    () => plans?.find((candidat) => candidat.id === planChoisi) ?? null,
    [plans, planChoisi],
  );
  const version = useMemo(
    () => plan?.versions.find((candidate) => candidate.id === versionChoisie) ?? null,
    [plan, versionChoisie],
  );

  /** L'historique d'activation suit le PLAN choisi, et lui seul. */
  useEffect(() => {
    if (planChoisi === null) {
      setActivations(null);
      return undefined;
    }
    let vivant = true;
    void (async () => {
      const liste = await fetchPlanActivations(client, planChoisi).catch(() => null);
      if (vivant) setActivations(liste);
    })();
    return () => {
      vivant = false;
    };
  }, [client, planChoisi]);

  /** Choisir une version efface ce qui portait sur la précédente. */
  const choisirLaVersion = useCallback((id: string) => {
    setVersionChoisie(id);
    setRejeu(null);
    setFantome(null);
    setComparaison(null);
    setErreurImpact(null);
    setErreurGeste(null);
  }, []);

  const mesurerLImpact = useCallback(async (): Promise<void> => {
    if (version === null) return;
    setErreurImpact(null);
    try {
      // Les deux ensemble : le rejeu dit ce que la version CHANGERAIT, le
      // fantôme ce qu'elle a DÉJÀ observé. Les lire séparément ferait décider
      // sur une moitié.
      const [impact, ecarts] = await Promise.all([
        replayPricing(client, version.id),
        fetchShadowObservation(client, version.id).catch(() => null),
      ]);
      setRejeu(impact);
      setFantome(ecarts);
    } catch (echec) {
      setRejeu(null);
      setFantome(null);
      setErreurImpact(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client, version]);

  const comparer = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (version === null) return;
      setErreurImpact(null);
      try {
        setComparaison(
          await comparePlanVersions(client, valeurs.left ?? '', version.id),
        );
      } catch (echec) {
        setComparaison(null);
        setErreurImpact(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, version],
  );

  const simuler = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      try {
        setSimulation(
          await simulatePricing(client, {
            accountId: valeurs.accountId ?? '',
            billableEvent: valeurs.billableEvent ?? '',
            amount: Number(valeurs.amount ?? '0'),
            channel: valeurs.channel,
            promoCode: valeurs.promoCode,
          }),
        );
        setErreurSimulation(null);
      } catch (echec) {
        setSimulation(null);
        setErreurSimulation(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client],
  );

  const agir = useCallback(
    async (action: string, entityRef: string, valeurs: Readonly<Record<string, string>>) => {
      const { reason, ...charge } = valeurs;
      try {
        const issue = await performPricingAction(client, {
          action,
          entityRef,
          reason: reason ?? '',
          payload: charge,
        });
        setErreurGeste(null);
        // **Le compte rendu vient du serveur, et `outcome` est rendu tel quel.**
        // `PRICING_PLAN_ACTIVATE` est en double validation : le socle rend 202
        // avec l'identifiant de la demande, et l'écran ne doit PAS annoncer un
        // tarif en vigueur. C'est exactement la faute qu'un écran commettrait
        // en composant sa propre phrase.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? issue.detail
            : t('pricing.pending', { id: issue.approvalRequestId ?? '' }),
        );
        await relireLesPlans();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, relireLesPlans, t],
  );

  return (
    <section aria-label={t('pricing.landmark')} className="pricing">
      <p className="pricing__journal-notice">{t('pricing.journalNotice')}</p>

      {avis === null ? null : (
        <p className="pricing__avis" role="status">
          {avis}
        </p>
      )}

      {erreurPlans === null ? null : (
        <p className="pricing__notice" role="alert">
          {t(erreurPlans)}
        </p>
      )}

      <div className="pricing__work">
        <PlanRail
          onChoosePlan={(id) => {
            setPlanChoisi(id);
            setVersionChoisie(null);
            setRejeu(null);
            setFantome(null);
            setComparaison(null);
          }}
          onChooseVersion={choisirLaVersion}
          planId={planChoisi}
          plans={plans}
          versionId={versionChoisie}
        />

        <div className="pricing__result">
          {version === null || plan === null ? (
            <SystemState
              bodyKey="pricing.chooseBody"
              testId="pricing-choose"
              titleKey="pricing.chooseTitle"
            />
          ) : (
            <VersionPanel
              activations={activations}
              comparison={comparaison}
              erreurGeste={erreurGeste}
              erreurImpact={erreurImpact}
              onAct={agir}
              onCompare={comparer}
              onMeasure={() => {
                void mesurerLImpact();
              }}
              plan={plan}
              replay={rejeu}
              shadow={fantome}
              version={version}
            />
          )}
        </div>
      </div>

      <SimulatorSection
        error={erreurSimulation}
        onSubmit={simuler}
        simulation={simulation}
      />

      <ProfitabilitySection error={erreurRentabilite} profitability={rentabilite} />
    </section>
  );
}

/** Le rail : les plans, et les versions du plan choisi. */
function PlanRail({
  onChoosePlan,
  onChooseVersion,
  planId,
  plans,
  versionId,
}: {
  readonly onChoosePlan: (id: string) => void;
  readonly onChooseVersion: (id: string) => void;
  readonly planId: string | null;
  readonly plans: readonly PricingPlan[] | null;
  readonly versionId: string | null;
}): ReactNode {
  const { t } = useTranslation();

  if (plans === null) {
    return (
      <div className="pricing__rail">
        <p className="pricing__idle">{t('pricing.loading')}</p>
      </div>
    );
  }
  if (plans.length === 0) {
    return (
      <div className="pricing__rail">
        <SystemState
          bodyKey="pricing.plansEmptyBody"
          testId="pricing-plans-empty"
          titleKey="pricing.plansEmptyTitle"
        />
      </div>
    );
  }

  return (
    <div className="pricing__rail" data-testid="pricing-rail">
      <h2 className="pricing__section-title">{t('pricing.plansTitle')}</h2>
      <ul aria-label={t('pricing.plansTitle')} className="pricing__plans" role="listbox">
        {plans.map((plan) => (
          <li
            aria-selected={plan.id === planId}
            className="pricing__plan"
            data-chosen={plan.id === planId}
            data-testid={`pricing-plan-${plan.code}`}
            key={plan.id}
            onClick={() => {
              onChoosePlan(plan.id);
            }}
            role="option"
          >
            <span className="pricing__plan-name">{plan.name}</span>
            <span className="pricing__plan-meta">
              {t('pricing.planMeta', { code: plan.code, scope: plan.scope })}
            </span>
            {/* Le plan par DÉFAUT se dit : c'est celui qui s'applique à qui
                n'est rattaché à aucun autre, et l'activer n'a pas la même
                portée qu'activer une grille de niche. */}
            {plan.isDefault ? (
              <span className="pricing__badge" data-tone="info">
                {t('pricing.defaultPlan')}
              </span>
            ) : null}

            {plan.id === planId ? (
              <ul className="pricing__versions">
                {plan.versions.map((version) => (
                  <li
                    className="pricing__version"
                    data-chosen={version.id === versionId}
                    data-testid={`pricing-version-${String(version.versionNo)}`}
                    key={version.id}
                  >
                    <button
                      className="pricing__version-button"
                      onClick={(evenement) => {
                        evenement.stopPropagation();
                        onChooseVersion(version.id);
                      }}
                      type="button"
                    >
                      <span className="pricing__version-no">
                        {t('pricing.versionNo', { no: version.versionNo })}
                      </span>
                      <span className="pricing__version-status" data-status={version.status}>
                        {version.status}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** La version choisie : son état, son historique, son impact, ses gestes. */
function VersionPanel({
  activations,
  comparison,
  erreurGeste,
  erreurImpact,
  onAct,
  onCompare,
  onMeasure,
  plan,
  replay,
  shadow,
  version,
}: {
  readonly activations: readonly PlanActivation[] | null;
  readonly comparison: readonly RuleComparison[] | null;
  readonly erreurGeste: string | null;
  readonly erreurImpact: string | null;
  readonly onAct: (
    action: string,
    entityRef: string,
    valeurs: Readonly<Record<string, string>>,
  ) => void | Promise<void>;
  readonly onCompare: (valeurs: Readonly<Record<string, string>>) => void | Promise<void>;
  readonly onMeasure: () => void;
  readonly plan: PricingPlan;
  readonly replay: PricingReplay | null;
  readonly shadow: ShadowObservation | null;
  readonly version: PlanVersion;
}): ReactNode {
  const { t } = useTranslation();
  const [geste, setGeste] = useState<string | null>(null);

  const champsDuGeste: readonly FormFieldDescriptor[] = useMemo(() => {
    const motif: FormFieldDescriptor = {
      name: 'reason',
      labelKey: 'pricing.fields.reason',
      control: 'text',
      placeholderKey: 'pricing.fields.reasonPlaceholder',
      required: true,
    };
    if (geste === pricingActions.activate) {
      return [
        {
          name: 'mode',
          labelKey: 'pricing.fields.mode',
          control: 'select',
          placeholderKey: 'pricing.fields.modePlaceholder',
          required: true,
          options: activationModes.map((mode) => ({
            value: mode,
            label: t(`pricing.modes.${mode}`),
          })),
        },
        {
          name: 'rolloutPercentage',
          labelKey: 'pricing.fields.rollout',
          control: 'text',
          placeholderKey: 'pricing.fields.rolloutPlaceholder',
        },
        motif,
      ];
    }
    if (geste === pricingActions.draft) {
      return [
        {
          name: 'effectiveFrom',
          labelKey: 'pricing.fields.effectiveFrom',
          control: 'date',
          placeholderKey: 'pricing.fields.effectiveFromPlaceholder',
          required: true,
        },
        motif,
      ];
    }
    return [motif];
  }, [geste, t]);

  const autresVersions = useMemo(
    () => plan.versions.filter((candidate) => candidate.id !== version.id),
    [plan, version],
  );

  return (
    <div className="pricing__version-panel" data-testid="pricing-version-panel">
      <h2 className="pricing__title" data-testid="pricing-version-title">
        {t('pricing.versionTitle', { name: plan.name, no: version.versionNo })}
      </h2>

      <dl className="pricing__facts">
        <Fact label={t('pricing.facts.status')} value={version.status} />
        <Fact label={t('pricing.facts.from')} value={version.effectiveFrom} />
        <Fact
          label={t('pricing.facts.to')}
          value={version.effectiveTo ?? t('pricing.facts.open')}
        />
        <Fact
          label={t('pricing.facts.rollout')}
          value={t('pricing.facts.percent', { value: version.rolloutPercentage })}
        />
        <Fact
          label={t('pricing.facts.grandfather')}
          value={version.grandfatherFrom ?? t('pricing.facts.noGrandfather')}
        />
        {/* **Le second signataire ne se suppose pas.** `approvedBy` absent
            signifie « pas encore signé deux fois », et le remplacer par le
            demandeur ferait croire qu'une personne a signé pour deux — ce que
            la double validation existe pour empêcher. */}
        <Fact
          label={t('pricing.facts.approvedBy')}
          value={version.approvedBy ?? t('pricing.facts.notApproved')}
        />
        <Fact
          label={t('pricing.facts.changeReason')}
          value={version.changeReason ?? t('pricing.facts.noReason')}
        />
      </dl>

      {/* --- L'impact : rien ne part tout seul --- */}
      <section aria-label={t('pricing.impactTitle')} className="pricing__impact">
        <h3 className="pricing__section-title">{t('pricing.impactTitle')}</h3>
        <p className="pricing__meta">{t('pricing.impactBody')}</p>
        <button className="pricing__measure" onClick={onMeasure} type="button">
          {t('pricing.measure')}
        </button>

        {erreurImpact === null ? null : (
          <p className="pricing__notice" role="alert">
            {t(erreurImpact)}
          </p>
        )}

        {replay === null ? null : <ReplayResult replay={replay} />}
        {shadow === null ? null : <ShadowResult shadow={shadow} />}
      </section>

      {/* --- La comparaison --- */}
      {autresVersions.length === 0 ? null : (
        <section aria-label={t('pricing.compareTitle')} className="pricing__compare">
          <h3 className="pricing__section-title">{t('pricing.compareTitle')}</h3>
          <FormShell
            fields={[
              {
                name: 'left',
                labelKey: 'pricing.fields.against',
                control: 'select',
                placeholderKey: 'pricing.fields.againstPlaceholder',
                required: true,
                options: autresVersions.map((candidate) => ({
                  value: candidate.id,
                  label: t('pricing.fields.againstOption', {
                    no: candidate.versionNo,
                    status: candidate.status,
                  }),
                })),
              },
            ]}
            layout="inline"
            onSubmit={onCompare}
            submitLabelKey="pricing.compare"
          />
          {comparison === null ? null : <ComparisonResult comparison={comparison} />}
        </section>
      )}

      {/* --- L'historique d'activation --- */}
      <section aria-label={t('pricing.historyTitle')} className="pricing__history">
        <h3 className="pricing__section-title">{t('pricing.historyTitle')}</h3>
        {activations === null ? (
          <p className="pricing__idle">{t('pricing.loading')}</p>
        ) : activations.length === 0 ? (
          <p className="pricing__idle" data-testid="pricing-history-empty">
            {t('pricing.historyEmpty')}
          </p>
        ) : (
          <ul className="pricing__events" data-testid="pricing-history">
            {activations.map((entree) => (
              <li className="pricing__event" key={entree.id}>
                <span className="pricing__event-type">{entree.operation}</span>
                <span className="pricing__event-meta">{entree.occurredAt}</span>
                <span className="pricing__event-meta">
                  {entree.approvedBy === null
                    ? t('pricing.facts.notApproved')
                    : t('pricing.facts.approvedShort', { by: entree.approvedBy })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- Les trois gestes --- */}
      <section aria-label={t('pricing.gesturesTitle')} className="pricing__gestures">
        <h3 className="pricing__section-title">{t('pricing.gesturesTitle')}</h3>
        <p className="pricing__meta">{t('pricing.gesturesBody')}</p>
        <div className="pricing__gesture-buttons">
          {[
            { id: pricingActions.draft, key: 'pricing.gestures.draft' },
            { id: pricingActions.activate, key: 'pricing.gestures.activate' },
            { id: pricingActions.rollback, key: 'pricing.gestures.rollback' },
          ].map((entree) => (
            <button
              className="pricing__gesture"
              data-chosen={geste === entree.id}
              key={entree.id}
              onClick={() => {
                setGeste(geste === entree.id ? null : entree.id);
              }}
              type="button"
            >
              {t(entree.key)}
            </button>
          ))}
        </div>

        {erreurGeste === null ? null : (
          <p className="pricing__notice" role="alert">
            {t(erreurGeste)}
          </p>
        )}

        {geste === null ? null : (
          <FormShell
            fields={champsDuGeste}
            layout="stacked"
            onSubmit={(valeurs) =>
              // **L'objet du geste n'est pas le même pour les trois.** Le
              // brouillon et le retour arrière portent sur le PLAN ; l'activation
              // porte sur la VERSION. Se tromper d'objet ferait refuser l'action
              // par le socle, et le refus se lirait comme un défaut de droit.
              onAct(
                geste,
                geste === pricingActions.activate ? version.id : plan.id,
                valeurs,
              )
            }
            submitLabelKey="pricing.gestureSubmit"
          />
        )}
      </section>
    </div>
  );
}

/** Le rejeu : ce que la version CHANGERAIT sur le trafic réel passé. */
function ReplayResult({ replay }: { readonly replay: PricingReplay }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="pricing__replay" data-testid="pricing-replay">
      <p className="pricing__answer" data-testid="pricing-delta">
        {t('pricing.replay.delta', { value: replay.deltaRevenue })}
      </p>
      <p className="pricing__meta">
        {t('pricing.replay.meta', {
          examined: replay.examined,
          from: replay.from,
          to: replay.to,
        })}
      </p>
      {/* **`unpriced` ne se tait jamais.** Une opération que la version
          candidate ne sait pas tarifer n'est pas une opération à zéro franc :
          c'est un trou dans la grille, et le delta ci-dessus l'ignore. */}
      <p
        className="pricing__unpriced"
        data-testid="pricing-unpriced"
        data-tone={replay.unpriced > 0 ? 'warning' : undefined}
      >
        {replay.unpriced > 0
          ? t('pricing.replay.unpriced', { count: replay.unpriced })
          : t('pricing.replay.allPriced')}
      </p>
      <p className="pricing__meta" data-testid="pricing-replica">
        {replay.servedByReplica
          ? t('pricing.replay.replica')
          : t('pricing.replay.primary')}
      </p>
    </div>
  );
}

/** Le mode fantôme : ce que la version a DÉJÀ observé sans l'appliquer. */
function ShadowResult({ shadow }: { readonly shadow: ShadowObservation }): ReactNode {
  const { t } = useTranslation();
  if (shadow.bySegment.length === 0) {
    return (
      <p className="pricing__idle" data-testid="pricing-shadow-empty">
        {t('pricing.shadow.empty')}
      </p>
    );
  }
  return (
    <ul className="pricing__segments" data-testid="pricing-shadow">
      {shadow.bySegment.map((segment) => (
        <li className="pricing__segment" key={segment.segmentCode}>
          <span className="pricing__segment-code">{segment.segmentCode}</span>
          <span className="pricing__segment-meta">
            {t('pricing.shadow.segment', {
              observed: segment.observed,
              delta: segment.deltaFrancs,
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** La comparaison, règle par règle. */
function ComparisonResult({
  comparison,
}: {
  readonly comparison: readonly RuleComparison[];
}): ReactNode {
  const { t } = useTranslation();
  if (comparison.length === 0) {
    return (
      <p className="pricing__idle" data-testid="pricing-compare-empty">
        {t('pricing.compareEmpty')}
      </p>
    );
  }
  return (
    <ul className="pricing__segments" data-testid="pricing-compare">
      {comparison.map((ligne) => (
        <li className="pricing__segment" key={ligne.matchKey}>
          <span className="pricing__segment-code">{ligne.matchKey}</span>
          <span className="pricing__segment-meta">{ligne.verdict}</span>
          {/* **Une règle absente d'un côté se DIT.** La rendre par une règle à
              zéro ferait lire « gratuit » là où il faut lire « aucune règle ». */}
          <span className="pricing__segment-meta">
            {ligne.left === null
              ? t('pricing.compareOnlyRight')
              : ligne.right === null
                ? t('pricing.compareOnlyLeft')
                : t('pricing.comparePayer', { payer: ligne.right.payer })}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Le simulateur : une section à part, parce que c'est un autre geste. */
function SimulatorSection({
  error,
  onSubmit,
  simulation,
}: {
  readonly error: string | null;
  readonly onSubmit: (valeurs: Readonly<Record<string, string>>) => void | Promise<void>;
  readonly simulation: PricingSimulation | null;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <section aria-label={t('pricing.simulateTitle')} className="pricing__simulator">
      <h2 className="pricing__section-title">{t('pricing.simulateTitle')}</h2>
      <p className="pricing__meta">{t('pricing.simulateBody')}</p>
      <FormShell
        fields={[
          {
            name: 'accountId',
            labelKey: 'pricing.fields.account',
            control: 'text',
            placeholderKey: 'pricing.fields.accountPlaceholder',
            required: true,
          },
          {
            name: 'billableEvent',
            labelKey: 'pricing.fields.event',
            control: 'text',
            placeholderKey: 'pricing.fields.eventPlaceholder',
            required: true,
          },
          {
            name: 'amount',
            labelKey: 'pricing.fields.amount',
            control: 'text',
            placeholderKey: 'pricing.fields.amountPlaceholder',
            required: true,
          },
          {
            name: 'promoCode',
            labelKey: 'pricing.fields.promo',
            control: 'text',
            placeholderKey: 'pricing.fields.promoPlaceholder',
          },
        ]}
        layout="inline"
        onSubmit={onSubmit}
        submitLabelKey="pricing.simulate"
      />

      {error === null ? null : (
        <p className="pricing__notice" role="alert">
          {t(error)}
        </p>
      )}

      {simulation === null ? null : (
        <div className="pricing__simulation" data-testid="pricing-simulation">
          <p className="pricing__answer" data-testid="pricing-fee">
            {t('pricing.simulation.fee', { value: simulation.feeAmount })}
          </p>
          <p className="pricing__meta">
            {t('pricing.simulation.meta', {
              event: simulation.billableEvent,
              base: simulation.baseAmount,
              payer: simulation.payer,
            })}
          </p>
          {/* **La trace est ce qui distingue une simulation d'un chiffre.** Un
              administrateur tarifaire ne demande pas seulement combien, il
              demande pourquoi — et sans les règles examinées, il ne peut pas
              corriger la grille. */}
          {simulation.trace.length === 0 ? (
            <p className="pricing__idle" data-testid="pricing-no-trace">
              {t('pricing.simulation.noTrace')}
            </p>
          ) : (
            <ol className="pricing__trace" data-testid="pricing-trace">
              {simulation.trace.map((etape) => (
                <li className="pricing__trace-step" key={etape}>
                  {etape}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

/** La rentabilité par segment et par guichet. */
function ProfitabilitySection({
  error,
  profitability,
}: {
  readonly error: string | null;
  readonly profitability: Profitability | null;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <section aria-label={t('pricing.profitTitle')} className="pricing__profit">
      <h2 className="pricing__section-title">{t('pricing.profitTitle')}</h2>
      {error !== null ? (
        <p className="pricing__notice" role="alert">
          {t(error)}
        </p>
      ) : profitability === null ? (
        <p className="pricing__idle">{t('pricing.loading')}</p>
      ) : profitability.bySegment.length === 0 ? (
        <SystemState
          bodyKey="pricing.profitEmptyBody"
          testId="pricing-profit-empty"
          titleKey="pricing.profitEmptyTitle"
        />
      ) : (
        <>
          <p className="pricing__meta">
            {t('pricing.profitMeta', {
              from: profitability.from,
              to: profitability.to,
            })}
          </p>
          <div className="pricing__table-frame">
            <table className="pricing__table" data-testid="pricing-profit">
              <thead>
                <tr>
                  <th scope="col">{t('pricing.profit.segment')}</th>
                  <th scope="col">{t('pricing.profit.operations')}</th>
                  <th scope="col">{t('pricing.profit.revenue')}</th>
                  <th scope="col">{t('pricing.profit.cost')}</th>
                  <th scope="col">{t('pricing.profit.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {profitability.bySegment.map((ligne) => (
                  <tr data-testid={`pricing-profit-${ligne.key}`} key={ligne.key}>
                    <td>{ligne.label}</td>
                    <td data-emphasis="numeric">{ligne.operations}</td>
                    <td data-emphasis="numeric">{ligne.revenue}</td>
                    <td data-emphasis="numeric">
                      {ligne.distributionCost + ligne.promotionCost}
                    </td>
                    {/* Une marge NÉGATIVE se dit par le signe ET par la teinte :
                        c'est celle qui décide de refaire une grille. */}
                    <td data-emphasis="numeric" data-tone={ligne.netMargin < 0 ? 'danger' : undefined}>
                      {ligne.netMargin}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/** Un fait du panneau : un intitulé, une valeur. */
function Fact({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): ReactNode {
  return (
    <div className="pricing__fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Un état de zone. Voir `ReportingScreen` pour ce que trois `<p>` gris coûtent. */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="pricing__state" data-testid={testId}>
      <span aria-hidden="true" className="pricing__state-emblem" />
      <p className="pricing__state-title">{t(titleKey)}</p>
      <p className="pricing__state-body">{t(bodyKey)}</p>
    </div>
  );
}
